/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-07-25 12:07:54
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Field, FieldArray } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import SpecificCollections from './specific-collections';
import { TextStyle } from '@shopify/polaris';
import ConfirmDisableModal from './confirm-disable-modal';

type Props = {
  defaultSorting: Object,
  sortOptions: Object,
  updateCustomSortingForm: (index: number, data: Object) => void
};
type ComposedProps = Props & WithI18nProps;

type State = {
  activeModalDisable: boolean
};

class DefaultSortingOptions extends Component<ComposedProps, State> {
  state = {
    activeModalDisable: false
  };

  render() {
    const { activeModalDisable } = this.state;
    const { loading, defaultSorting, sortOptions, i18n, updateCustomSortingForm } = this.props;
    const {
      status = false,
      statusSearch = false,
      statusCollection = false,
      specificCollections = []
    } = defaultSorting;
    const optionSorts = this.buildOptionSorts(sortOptions);
    const optionSortsSearch = _.filter(optionSorts, item => item.type !== 'manual');

    return (
      <>
        <div className="groupFields">
          <p>{i18n.translate('AdminHTML.CustomSorting.DefaultSortingOptions.content')}</p>
          <div className="groupFields-item">
            <Field
              name="defaultSorting.status"
              component={RenderToggleSwitch}
              label={i18n.translate('AdminHTML.CustomSorting.DefaultSortingOptions.statusOptions')}
              onChange={e => this.handleChangeStatus(e)}
            />
          </div>
          {status && (
            <>
              <div className="groupFields-item">
                <Field
                  name="defaultSorting.statusSearch"
                  component={RenderCheckbox}
                  label={i18n.translate(
                    'AdminHTML.CustomSorting.DefaultSortingOptions.statusSearch'
                  )}
                  tooltip={i18n.translate(
                    'AdminHTML.CustomSorting.DefaultSortingOptions.statusSearch_Tooltip',
                    {
                      t1: (
                        <TextStyle variation="strong">
                          {i18n.translate(
                            'AdminHTML.CustomSorting.DefaultSortingOptions.statusSearch_Tooltip_t1'
                          )}
                        </TextStyle>
                      )
                    }
                  )}
                />
                <div className="groupFields-item-child">
                  <Field
                    name="defaultSorting.search"
                    options={optionSortsSearch}
                    component={RenderSelect}
                    disabled={!statusSearch || loading}
                    placeholder={i18n.translate(
                      'AdminHTML.CustomSorting.DefaultSortingOptions.search_Placeholder'
                    )}
                  />
                </div>
              </div>
              <div className="groupFields-item">
                <Field
                  name="defaultSorting.statusCollection"
                  component={RenderCheckbox}
                  label={i18n.translate(
                    'AdminHTML.CustomSorting.DefaultSortingOptions.statusCollection'
                  )}
                />
                <div className="groupFields-item-child">
                  <Field
                    name="defaultSorting.allCollection"
                    label={i18n.translate(
                      'AdminHTML.CustomSorting.DefaultSortingOptions.allCollection'
                    )}
                    tooltip={i18n.translate(
                      'AdminHTML.CustomSorting.DefaultSortingOptions.allCollection_Tooltip'
                    )}
                    options={optionSorts}
                    component={RenderSelect}
                    disabled={!statusCollection || loading}
                    placeholder={i18n.translate(
                      'AdminHTML.CustomSorting.DefaultSortingOptions.allCollection_Placeholder'
                    )}
                  />
                  <div style={{ marginTop: '1.25rem' }}>
                    <FieldArray
                      name="defaultSorting.specificCollections"
                      component={SpecificCollections}
                      key={'specific-collections'}
                      disabledAddNew={
                        !statusCollection || specificCollections.length === sortOptions.length
                      }
                      disabledActions={!statusCollection}
                      specificCollections={specificCollections}
                      optionSorts={optionSorts}
                      updateCustomSortingForm={updateCustomSortingForm}
                      loading={loading}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        {/* Confirm disable modal */}
        <ConfirmDisableModal
          active={activeModalDisable}
          handleChange={this.handleActiveOptionModalDisable}
          handleDisable={this.disableDefaultSortOptions}
          title={i18n.translate(
            'AdminHTML.CustomSorting.DefaultSortingOptions.confirmDisable_Title'
          )}
        />
      </>
    );
  }

  // Handle change status
  handleChangeStatus = e => {
    const {
      defaultSorting: { status = false }
    } = this.props;

    if (status) {
      e.preventDefault();
      this.handleActiveOptionModalDisable();
    }
  };

  // Show/Hide disable modal
  handleActiveOptionModalDisable = () => {
    this.setState(({ activeModalDisable }) => ({
      activeModalDisable: !activeModalDisable
    }));
  };

  disableDefaultSortOptions = () => {
    const { updateCustomSortingForm } = this.props;

    updateCustomSortingForm('defaultSorting', { status: false });
    this.handleActiveOptionModalDisable();
  };

  // Build list sort option
  buildOptionSorts = sortOptions => {
    let result = [];
    if (_.isEmpty(sortOptions)) {
      return result;
    }
    _.map(sortOptions, item => {
      if (item.status === 'active') {
        let { label = '' } = item;
        if (label === '') {
          label = item.type;
          label += item.order ? ` - ${item.order}` : '';
        }
        result.push({ value: item.id, label, type: item.type });
      }
    });

    return result;
  };
}
export default withI18n()(DefaultSortingOptions);
